import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faBars, faXmark, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import CartIcon from './CartIcon';
import SearchBox from './SearchBox';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `relative transition-colors duration-300 group font-normal ${
    isActive ? 'text-sage-700' : 'text-earth-700 hover:text-sage-700'
  }`;

const NavItem: React.FC<{ to: string; label: string; onClick?: () => void; mobile?: boolean }> = ({
  to, label, onClick, mobile,
}) => {
  if (mobile) {
    return (
      <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
          `block py-3 text-lg transition-colors duration-300 ${
            isActive ? 'text-sage-700 font-medium' : 'text-earth-700 hover:text-sage-700'
          }`
        }
      >
        {label}
      </NavLink>
    );
  }
  return (
    <NavLink to={to} className={navLinkClass}>
      <span>{label}</span>
      <span className="absolute bottom-0 left-0 w-0 h-px bg-sage-600 transition-all duration-300 group-hover:w-full"></span>
    </NavLink>
  );
};

const Header: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const closeAll = () => { setMenuOpen(false); setSearchOpen(false); };

  return (
    <header className="sticky top-0 z-40 bg-cream-50/90 backdrop-blur border-b border-earth-200" style={{
      boxShadow: '0 1px 3px rgba(61, 53, 42, 0.05)'
    }}>
      <div className="container-custom">
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" onClick={closeAll}>
            <motion.div
              whileHover={{ rotate: 8, scale: 1.05 }}
              className="text-sage-600 transition-colors duration-300 group-hover:text-sage-700"
              transition={{ type: "spring", stiffness: 300 }}
            >
              <FontAwesomeIcon icon={faShoppingCart} className="h-6 w-6" />
            </motion.div>
            <span className="text-2xl font-display font-light text-earth-900 tracking-wide">GoodCart</span>
          </Link>

          {/* Search Bar (desktop) */}
          <div className="hidden md:block flex-1 max-w-lg mx-8">
            <SearchBox />
          </div>

          {/* Navigation (desktop) */}
          <nav className="hidden md:flex items-center space-x-7">
            <NavItem to="/categories" label="Categories" />
            <NavItem to="/brands" label="Brands" />
            <NavItem to="/search" label="Shop" />
            <NavItem to="/lists" label="Lists" />
            <NavItem to="/saved" label="Saved" />
            <CartIcon />
          </nav>

          {/* Mobile: search icon, cart, menu toggle */}
          <div className="md:hidden flex items-center gap-1">
            <button
              type="button"
              aria-label="Search"
              aria-expanded={searchOpen}
              onClick={() => { setSearchOpen(!searchOpen); setMenuOpen(false); }}
              className="p-2.5 text-earth-700 rounded-full hover:bg-sage-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} className="h-4 w-4" />
            </button>
            <CartIcon />
            <button
              type="button"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => { setMenuOpen(!menuOpen); setSearchOpen(false); }}
              className="p-2.5 text-earth-700 rounded-full hover:bg-sage-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 transition-colors"
            >
              <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile search overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="md:hidden border-t border-earth-200 bg-cream-50 px-4 py-3"
          >
            <SearchBox autoFocus large />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden overflow-hidden border-t border-earth-200 bg-cream-50"
          >
            <div className="p-5 divide-y divide-earth-100">
              <NavItem to="/categories" label="Categories" mobile onClick={closeAll} />
              <NavItem to="/brands" label="Brands" mobile onClick={closeAll} />
              <NavItem to="/search" label="Shop all" mobile onClick={closeAll} />
              <NavItem to="/lists" label="Lists" mobile onClick={closeAll} />
              <NavItem to="/saved" label="Saved picks" mobile onClick={closeAll} />
              <NavItem to="/about" label="About" mobile onClick={closeAll} />
              <NavItem to="/cart" label="Cart" mobile onClick={closeAll} />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
